// Modified Script.js
// Changes:
// - Made strategies dynamic, fetched from backend
// - Added form to add new strategy with name
// - Updated getStrategyStatus to check by strategy name in process key
// - Handled dynamic tabs and forms for each strategy
// - Defaults set when adding new strategy
// - ADDED: Delete strategy button and handler function

import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import * as api from '../../api/api.js';
import { toast } from 'react-toastify';

// Utility function to sanitize strategy names for CSS selectors
const sanitizeId = (str) => {
    return str.replace(/\s+/g, '-').toLowerCase();
};

const OwnAlgo = () => {
    const [status, setStatus] = useState({ trading_active: false, accounts: {} });
    const [strategies, setStrategies] = useState([]);
    const [strategyConfigs, setStrategyConfigs] = useState({});
    const [formData, setFormData] = useState({});
    const [newStrategyName, setNewStrategyName] = useState('');

    // Fetch status and strategy configs on component mount
    useEffect(() => {
        const fetchStatus = async () => {
            try {
                const response = await api.getStatus();
                setStatus(response.data || { trading_active: false, accounts: {} });
            } catch (error) {
                console.error('Failed to fetch status:', error);
                toast.error('Failed to fetch trading status.');
            }
        };

        const fetchStrategyConfigs = async () => {
            try {
                const response = await api.getOwnStrategyConfigs();
                const configs = {};
                const stratList = [];
                response.data.forEach(config => {
                    stratList.push(config.strategy_name);
                    configs[config.strategy_name] = config.config;
                });
                setStrategies(stratList);
                setStrategyConfigs(configs);
                setFormData(configs);
            } catch (error) {
                console.error('Failed to fetch own strategy configs:', error);
                toast.error('Failed to fetch your strategy configurations.');
            }
        };

        // Initial fetch
        fetchStatus();
        fetchStrategyConfigs();

        // Poll status every 10 seconds
        const interval = setInterval(fetchStatus, 10000);
        return () => clearInterval(interval);
    }, []);

    // Handle adding new strategy
    const handleAddStrategy = async () => {
        const name = newStrategyName.trim();
        if (!name) {
            toast.error('Strategy name is required.');
            return;
        }
        if (strategies.includes(name)) {
            toast.error('Strategy name already exists.');
            return;
        }

        const defaultConfig = {
            trade_type: 'buy',
            buy_interval: 1.0,
            sell_interval: 1.0,
            exit_interval: 2.0,
            max_trades: 5,
            lot_size: 0.01,
            timeframe: 'M5',
            buy_limit: null,
            sell_limit: null,
            max_loss: null,
            pause_price: null
        };

        try {
            await api.updateOwnStrategyConfig(name, defaultConfig);
            setStrategies(prev => [...prev, name]);
            setStrategyConfigs(prev => ({ ...prev, [name]: defaultConfig }));
            setFormData(prev => ({ ...prev, [name]: defaultConfig }));
            setNewStrategyName('');
            toast.success('New strategy added successfully!');
        } catch (error) {
            console.error('Failed to add strategy:', error);
            toast.error('Failed to add strategy.');
        }
    };

    // NEW: Handle deleting a strategy
    const handleDeleteStrategy = async (strategyToDelete) => {
        // Prevent deletion if trading is active or strategy process is running
        if (status.trading_active || getStrategyStatus(strategyToDelete)) {
            toast.error('Cannot delete strategy while trading is active or its processes are running.');
            return;
        }

        // Confirmation dialog
        if (!window.confirm(`Are you sure you want to delete the "${strategyToDelete}" strategy? This action cannot be undone.`)) {
            return;
        }

        try {
            // Assumes an API function `deleteStrategyConfig` exists in your api.js file
            await api.deleteOwnStrategyConfig(strategyToDelete);

            // Update state to remove the deleted strategy
            setStrategies(prev => prev.filter(s => s !== strategyToDelete));
            setStrategyConfigs(prev => {
                const newConfigs = { ...prev };
                delete newConfigs[strategyToDelete];
                return newConfigs;
            });
            setFormData(prev => {
                const newFormData = { ...prev };
                delete newFormData[strategyToDelete];
                return newFormData;
            });

            toast.success(`Strategy "${strategyToDelete}" deleted successfully!`);
        } catch (error) {
            console.error('Failed to delete strategy:', error);
            // Display specific error from backend if available
            const errorMessage = error.response?.data?.error || 'Failed to delete strategy.';
            toast.error(errorMessage);
        }
    };

    // Handle form input changes
    const handleInputChange = (strategy, field, value) => {
        if ((field === 'buy_limit' || field === 'sell_limit' || field === 'max_loss' || field === 'buy_interval' || field === 'sell_interval' || field === 'pause_price') && value === '') {
            value = null;
        } else if (field === 'buy_limit' || field === 'sell_limit' || field === 'max_loss' || field === 'buy_interval' || field === 'sell_interval' || field === 'pause_price') {
            value = value ? parseFloat(value) : null;
        } else if (field === 'exit_interval' || field === 'lot_size') {
            value = parseFloat(value) || 0;
        } else if (field === 'max_trades') {
            value = parseInt(value) || 0;
        }
        setFormData(prev => ({
            ...prev,
            [strategy]: {
                ...prev[strategy],
                [field]: value
            }
        }));
    };

    // Handle form submission to update strategy config
    const handleSubmit = async (strategy, e) => {
        e.preventDefault();
        // Check if any process for this strategy is running
        const isAnyProcessRunning = Object.keys(status.accounts).some(
            key => key.endsWith(`:${strategy}`) && status.accounts[key].is_running
        );
        if (status.trading_active || isAnyProcessRunning) {
            toast.error('Cannot update configuration while trading is active or strategy processes are running.');
            return;
        }
        try {
            await api.updateOwnStrategyConfig(strategy, formData[strategy]);
            toast.success('Strategy configuration updated successfully!');
            setStrategyConfigs(prev => ({
                ...prev,
                [strategy]: formData[strategy]
            }));
        } catch (error) {
            console.error('Failed to update strategy config:', error);
            toast.error('Failed to update configuration.');
        }
    };

    // Determine if a strategy has any running processes
    const getStrategyStatus = (strategy) => {
        return Object.keys(status.accounts).some(
            key => key.endsWith(`:${strategy}`) && status.accounts[key].is_running
        );
    };

    return (
        <div className="App container mt-4">
            <h1>Algo Trading Dashboard</h1>

            {/* Form to add new strategy */}
            <div className="mb-4">
                <h3>Add New Strategy</h3>
                <div className="input-group">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Enter strategy name"
                        value={newStrategyName}
                        onChange={(e) => setNewStrategyName(e.target.value)}
                    />
                    <button className="btn btn-success" onClick={handleAddStrategy}>
                        Add Strategy
                    </button>
                </div>
            </div>

            {/* Bootstrap Tabs for Strategies */}
            <ul className="nav nav-tabs mb-4">
                {strategies.map((strategy, index) => (
                    <li className="nav-item" key={strategy}>
                        <a
                            className={`nav-link ${index === 0 ? 'active' : ''}`}
                            id={`${sanitizeId(strategy)}-tab`}
                            data-bs-toggle="tab"
                            href={`#${sanitizeId(strategy)}`}
                            role="tab"
                            aria-controls={sanitizeId(strategy)}
                            aria-selected={index === 0}
                        >
                            {strategy}
                        </a>
                    </li>
                ))}
            </ul>

            {/* Tab Content */}
            <div className="tab-content">
                {strategies.map((strategy, index) => (
                    <div
                        className={`tab-pane fade ${index === 0 ? 'show active' : ''}`}
                        id={sanitizeId(strategy)}
                        role="tabpanel"
                        aria-labelledby={`${sanitizeId(strategy)}-tab`}
                        key={strategy}
                    >
                        <h2>{strategy} Strategy</h2>

                        <form onSubmit={(e) => handleSubmit(strategy, e)} className="mt-3">
                            {/* All form inputs (Trade Type, Intervals, Lot Size, etc.) remain here */}
                            
                            {/* ... (previous form fields) ... */}
                             <div className="mb-3">
                                <label className="form-label d-block">Trade Type</label>
                                <div className="form-check form-check-inline">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name={`${sanitizeId(strategy)}-tradeType`}
                                        id={`${sanitizeId(strategy)}-buy`}
                                        value="buy"
                                        checked={formData[strategy]?.trade_type === 'buy'}
                                        onChange={(e) => handleInputChange(strategy, 'trade_type', e.target.value)}
                                        disabled={status.trading_active || getStrategyStatus(strategy)}
                                    />
                                    <label className="form-check-label" htmlFor={`${sanitizeId(strategy)}-buy`}>Buy</label>
                                </div>
                                <div className="form-check form-check-inline">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name={`${sanitizeId(strategy)}-tradeType`}
                                        id={`${sanitizeId(strategy)}-sell`}
                                        value="sell"
                                        checked={formData[strategy]?.trade_type === 'sell'}
                                        onChange={(e) => handleInputChange(strategy, 'trade_type', e.target.value)}
                                        disabled={status.trading_active || getStrategyStatus(strategy)}
                                    />
                                    <label className="form-check-label" htmlFor={`${sanitizeId(strategy)}-sell`}>Sell</label>
                                </div>
                            </div>

                            {formData[strategy]?.trade_type === 'buy' ? (
                                <div className="mb-3">
                                    <label htmlFor={`${sanitizeId(strategy)}-buyLimit`} className="form-label">Buy Limit Price</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        id={`${sanitizeId(strategy)}-buyLimit`}
                                        value={formData[strategy]?.buy_limit ?? ''}
                                        onChange={(e) => handleInputChange(strategy, 'buy_limit', e.target.value)}
                                        disabled={status.trading_active || getStrategyStatus(strategy)}
                                        step="0.1"
                                        min="0.1"
                                        placeholder='Enter buy limit or leave empty'
                                    />
                                </div>
                            ) : (
                                <div className="mb-3">
                                    <label htmlFor={`${sanitizeId(strategy)}-sellLimit`} className="form-label">Sell Limit Price</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        id={`${sanitizeId(strategy)}-sellLimit`}
                                        value={formData[strategy]?.sell_limit ?? ''}
                                        onChange={(e) => handleInputChange(strategy, 'sell_limit', e.target.value)}
                                        disabled={status.trading_active || getStrategyStatus(strategy)}
                                        step="0.1"
                                        min="0.1"
                                        placeholder='Enter sell limit or leave empty'
                                    />
                                </div>
                            )}

                            {formData[strategy]?.trade_type === 'buy' ? (
                                <div className="mb-3">
                                    <label htmlFor={`${sanitizeId(strategy)}-buyInterval`} className="form-label">Buy Interval (Points Down)</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        id={`${sanitizeId(strategy)}-buyInterval`}
                                        value={formData[strategy]?.buy_interval ?? ''}
                                        onChange={(e) => handleInputChange(strategy, 'buy_interval', e.target.value)}
                                        disabled={status.trading_active || getStrategyStatus(strategy)}
                                        step="0.1"
                                        placeholder="Leave empty for sequential trading"
                                    />
                                </div>
                            ) : (
                                <div className="mb-3">
                                    <label htmlFor={`${sanitizeId(strategy)}-sellInterval`} className="form-label">Sell Interval (Points Up)</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        id={`${sanitizeId(strategy)}-sellInterval`}
                                        value={formData[strategy]?.sell_interval ?? ''}
                                        onChange={(e) => handleInputChange(strategy, 'sell_interval', e.target.value)}
                                        disabled={status.trading_active || getStrategyStatus(strategy)}
                                        step="0.1"
                                        placeholder="Leave empty for sequential trading"
                                    />
                                </div>
                            )}

                            

                            <div className="mb-3">
                                <label htmlFor={`${sanitizeId(strategy)}-exitInterval`} className="form-label">Exit Interval (Points for TP)</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    id={`${sanitizeId(strategy)}-exitInterval`}
                                    value={formData[strategy]?.exit_interval ?? ''}
                                    onChange={(e) => handleInputChange(strategy, 'exit_interval', e.target.value)}
                                    disabled={status.trading_active || getStrategyStatus(strategy)}
                                    step="0.1"
                                    min="0.1"
                                />
                            </div>

                             <div className="mb-3">
                                <label htmlFor={`${sanitizeId(strategy)}-maxTrades`} className="form-label">Max Concurrent Trades</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    id={`${sanitizeId(strategy)}-maxTrades`}
                                    value={formData[strategy]?.max_trades || 5}
                                    onChange={(e) => handleInputChange(strategy, 'max_trades', e.target.value)}
                                    disabled={status.trading_active || getStrategyStatus(strategy)}
                                    step="1"
                                    min="1"
                                />
                            </div>

                            <div className="mb-3">
                                <label htmlFor={`${sanitizeId(strategy)}-maxLoss`} className="form-label">Max Loss ($)</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    id={`${sanitizeId(strategy)}-maxLoss`}
                                    value={formData[strategy]?.max_loss ?? ''}
                                    onChange={(e) => handleInputChange(strategy, 'max_loss', e.target.value)}
                                    disabled={status.trading_active || getStrategyStatus(strategy)}
                                    step="1"
                                    min="1"
                                    placeholder="Leave empty to disable"
                                />
                            </div>

                            <div className="mb-3">
                                <label htmlFor={`${sanitizeId(strategy)}-pausePrice`} className="form-label">Pause Trading Price Threshold</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    id={`${sanitizeId(strategy)}-pausePrice`}
                                    value={formData[strategy]?.pause_price ?? ''}
                                    onChange={(e) => handleInputChange(strategy, 'pause_price', e.target.value)}
                                    disabled={status.trading_active || getStrategyStatus(strategy)}
                                    step="0.01"
                                    placeholder="Leave empty to disable"
                                />
                            </div>
                            
                            <div className="mb-3">
                                <label htmlFor={`${sanitizeId(strategy)}-lotSize`} className="form-label">Lot Size</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    id={`${sanitizeId(strategy)}-lotSize`}
                                    value={formData[strategy]?.lot_size || 0.01}
                                    onChange={(e) => handleInputChange(strategy, 'lot_size', e.target.value)}
                                    disabled={status.trading_active || getStrategyStatus(strategy)}
                                    step="0.01"
                                    min="0.01"
                                />
                            </div>
                           
                            {/* MODIFIED: Group buttons for better layout */}
                            <div className="d-flex gap-2">
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={status.trading_active || getStrategyStatus(strategy)}
                                >
                                    Update Configuration
                                </button>
                                <button
                                    type="button" 
                                    className="btn btn-danger"
                                    onClick={() => handleDeleteStrategy(strategy)}
                                    disabled={status.trading_active || getStrategyStatus(strategy)}
                                >
                                    Delete Strategy
                                </button>
                            </div>
                        </form>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default OwnAlgo;